import { useEffect, useRef, useState } from "react";
import { Play, Pause, Headphones } from "lucide-react";

/**
 * Voice preview player — plays Kristy's HeyGen narration for up to `previewSeconds`
 * (default 30). Stops automatically at the limit. Compact + brand-styled.
 * Falls back to nothing if no audioUrl is provided.
 *
 * Props:
 *   audioUrl      string  MP3/WAV URL
 *   previewSeconds number  default 30
 *   label         string  right-hand label (e.g. "Hear Kristy narrate this course")
 *   variant       "light" | "dark" (default light — used on white bg)
 *   testId        string  optional data-testid override
 */
export default function VoicePreview({
  audioUrl,
  previewSeconds = 30,
  label = "Hear Kristy narrate a sample",
  variant = "light",
  testId,
}) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => {
      const pct = Math.min(1, a.currentTime / previewSeconds);
      setProgress(pct);
      if (a.currentTime >= previewSeconds) {
        a.pause();
        a.currentTime = 0;
        setPlaying(false);
        setProgress(0);
      }
    };
    const onEnd = () => { setPlaying(false); setProgress(0); };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("ended", onEnd);
    };
  }, [previewSeconds]);

  if (!audioUrl) return null;

  const toggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  };

  const dark = variant === "dark";
  const wrap = dark
    ? "bg-white/10 border-white/15 text-white hover:bg-white/15"
    : "bg-brand-blush/70 border-brand-pink/20 text-neutral-900 hover:bg-brand-blush";

  return (
    <div
      data-testid={testId || "voice-preview"}
      className={`inline-flex items-center gap-3 rounded-full border pl-1.5 pr-4 py-1.5 backdrop-blur-sm ${wrap}`}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={toggle}
        data-testid={`${testId || "voice-preview"}-toggle`}
        className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition ${
          playing ? "bg-white text-brand-pink" : "bg-brand-pink text-white hover:scale-105"
        }`}
        aria-label={playing ? "Pause narration preview" : "Play narration preview"}
      >
        {playing ? <Pause className="w-4 h-4" fill="currentColor" /> : <Play className="w-4 h-4 ml-0.5" fill="currentColor" />}
      </button>
      <div className="flex flex-col min-w-0">
        <div className="flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase font-medium opacity-70 leading-none">
          <Headphones className="w-3 h-3" /> {previewSeconds}s Preview
        </div>
        <div className="text-xs leading-tight mt-1 truncate max-w-[220px]">{label}</div>
      </div>
      <div className={`ml-2 h-1 w-16 rounded-full overflow-hidden ${dark ? "bg-white/15" : "bg-white/70"}`}>
        <div
          className="h-full bg-brand-pink transition-[width] duration-200"
          style={{ width: `${Math.round(progress * 100)}%` }}
        />
      </div>
      <audio ref={audioRef} src={audioUrl} preload="none" />
    </div>
  );
}
